import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, map, Observable, Subject} from 'rxjs';

import {
  DocumentClassReclassifyStartResponse
} from '../../components/files/components/document-classes/document-class.models';
import {BackgroundProgress} from '../background-progress.model';
import {StateConfigService} from '../state/state-config.service';

interface ReclassificationBatchState {
  batchId: string;
  projectId: number;
  total: number;
  pendingFileIds: Set<string>;
  createdAt: number;
}

export interface ReclassificationBatchProgress {
  batchId: string;
  projectId: number;
  total: number;
  done: number;
  progress: number;
  createdAt: number;
}

/**
 * Mindestalter eines Batches, bevor er gegen den Serverzustand abgeglichen wird. Das Backend setzt
 * die Dateien in derselben Transaktion auf PROCESSING, die auch die Batch-Antwort erzeugt -- ein
 * jüngerer Serverstand kann sie also noch ohne diese Dateien gemeldet haben.
 */
const MIN_RECONCILE_AGE_MS = 10_000;

/**
 * Hoechstalter eines Batches. Abgeglichen wird ein Batch nur, solange die Dokumentenklassen-Seite
 * offen ist (nur sie ruft reconcileWithRunningFiles auf). Verliess man die Seite, blieb er fuer die
 * restliche Sitzung offen und lieferte beim Zurueckkommen wieder `total - done` als Zaehler -
 * obwohl serverseitig langst nichts mehr lief. Nach dieser Zeit gilt er als erledigt.
 */
const MAX_BATCH_AGE_MS = 15 * 60 * 1000;

export interface ReclassificationOverallProgress {
  progress: number;
  done: number;
  total: number;
}

@Injectable({providedIn: 'root'})
export class ReclassificationProgressService {
  private readonly stateConfigService = inject(StateConfigService);
  private readonly batchesSubject = new BehaviorSubject<Map<string, ReclassificationBatchState>>(new Map());
  public readonly batches$: Observable<ReclassificationBatchProgress[]> = this.batchesSubject.pipe(
    map(batches => this.toProgressList(batches)),
  );
  public readonly hasActiveBatches$: Observable<boolean> = this.batches$.pipe(
    map(batches => batches.length > 0),
  );
  public readonly overallProgress$: Observable<ReclassificationOverallProgress> = this.batches$.pipe(
    map(batches => this.computeOverallProgress(batches)),
  );
  private readonly batchCompletedSubject = new Subject<ReclassificationBatchProgress>();
  public readonly batchCompleted$ = this.batchCompletedSubject.asObservable();

  /**
   * Fortschritt für die Hintergrund-Badge, ein Eintrag je Projekt — gleiche Form wie bei Upload,
   * Löschen und Klassifizierung.
   *
   * Warum es das braucht: ein Lauf über die Dokumentenklassen-Verwaltung war außerhalb genau dieser
   * Seite unsichtbar. In der Dateiansicht sah man die Zeilen arbeiten, die Badge blieb aber leer —
   * sie kennt nur den `ClassificationService`, und dieser Lauf geht an ihm vorbei (der Server legt
   * den Stapel selbst an, das Frontend bekommt nur `queuedFileIds` zurück).
   *
   * Namen der laufenden Dateien gibt es hier nicht: die Antwort liefert nur IDs.
   */
  public readonly progressByProject$: Observable<BackgroundProgress[]> = this.batchesSubject.pipe(
    map(batches => {
      const now = Date.now();
      const byProject = new Map<number, {total: number, pending: number}>();

      batches.forEach(batch => {
        // Derselbe Altersriegel wie in collectPendingFileIds: ein Stapel, den niemand mehr
        // abgleicht, soll nicht ewig als laufend in der Badge stehen.
        if (now - batch.createdAt > MAX_BATCH_AGE_MS) return;
        const bucket = byProject.get(batch.projectId) ?? {total: 0, pending: 0};
        bucket.total += batch.total;
        bucket.pending += batch.pendingFileIds.size;
        byProject.set(batch.projectId, bucket);
      });

      return Array.from(byProject.entries()).map(([projectId, bucket]) => ({
        total: bucket.total,
        done: Math.max(0, bucket.total - bucket.pending),
        // Ein Fehlschlag kommt hier nicht an: die Datei landet auf Status ERROR und ist in der
        // Dateiliste sichtbar (wie beim ClassificationService).
        failed: 0,
        active: bucket.pending,
        currentNames: [],
        failedNames: [],
        projectId,
      }));
    }),
  );

  constructor() {
    this.stateConfigService.classificationCompleted$.subscribe(fileId => this.onFileCompleted(fileId));

    // Nach einer SSE-Unterbrechung aufholen. Der Strom holt nichts nach, und dieser Dienst schreibt
    // seinen Zustand ausschliesslich aus CLASSIFICATION_COMPLETED fort - ohne den Abgleich bliebe
    // ein Stapel, dessen Meldungen in die Luecke fielen, bis MAX_BATCH_AGE_MS stehen. sseReconnects$
    // feuert erst NACH updateData(), runningClassifications ist hier also schon der neue Stand.
    this.stateConfigService.sseReconnects$.subscribe(
      () => this.reconcileWithRunningFiles(this.stateConfigService.runningClassifications));

    // Und an jedem neuen Serverstand, nicht nur auf der Dokumentenklassen-Seite (die als einzige
    // pollt). Sonst haengt ein Stapel, dessen Abschlussmeldung fuer eine Datei nie ankam, bis zum
    // Altersriegel in der Badge - dieselbe Abweichung wie beim ClassificationService, dort steht die
    // ausfuehrliche Begruendung. Kostet keinen Request: die Liste kommt ohnehin herein.
    this.stateConfigService.runningClassificationsSet$.subscribe(
      running => this.reconcileWithRunningFiles(running));
  }

  public registerBatch(projectId: number, response: DocumentClassReclassifyStartResponse): void {
    if (!response?.batchId || !response.queuedFileIds?.length || response.queuedCount <= 0) {
      return;
    }

    const pendingFileIds = new Set(response.queuedFileIds);
    const next = new Map(this.batchesSubject.value);
    next.set(response.batchId, {
      batchId: response.batchId,
      projectId,
      total: response.queuedCount,
      pendingFileIds,
      createdAt: Date.now(),
    });
    this.batchesSubject.next(next);
  }

  /**
   * Noch offene Datei-IDs aller Batches eines Projekts.
   *
   * Grundlage der gemeinsamen Zaehlung laufender Klassifizierungen: gezaehlt wird eine ID-Menge,
   * nicht mehr `total - done` gegen `runningSet.size` (siehe classification-count.util.ts).
   */
  public pendingFileIdsForProject$(projectId: number): Observable<Set<string>> {
    return this.batchesSubject.pipe(
      map(batches => this.collectPendingFileIds(batches, projectId)),
    );
  }

  /** Synchrone Variante von {@link pendingFileIdsForProject$}. */
  public pendingFileIdsForProject(projectId: number): Set<string> {
    return this.collectPendingFileIds(this.batchesSubject.value, projectId);
  }

  /**
   * Gleicht die offenen Batches gegen den Serverzustand ab: jede Datei, die dort nicht mehr als
   * laufende Klassifizierung gemeldet wird, hat einen Endzustand erreicht (READY oder ERROR) --
   * auch dann, wenn ihr SSE-Event nie angekommen ist. Ohne diesen Abgleich blieb ein Batch nach
   * einem verlorenen Event fuer die gesamte Sitzung offen und der Fortschritt stehen.
   *
   * <p>Nur aus einem Poll heraus aufrufen, der den Serverzustand gerade frisch geholt hat. Der
   * Mindestalter-Guard verhindert, dass ein Serverstand, der aelter ist als der Batch selbst, ihn
   * sofort wieder abschliesst.
   */
  public reconcileWithRunningFiles(runningFileIds: Iterable<string>): void {
    const running = new Set(runningFileIds);
    const now = Date.now();
    const next = new Map(this.batchesSubject.value);
    let changed = false;
    const finished: ReclassificationBatchProgress[] = [];

    next.forEach((batch, batchId) => {
      if (now - batch.createdAt > MAX_BATCH_AGE_MS) {
        // Zu alt: als erledigt behandeln, damit kein Zombie-Zaehler zurueckkommt.
        finished.push(this.toProgress(batch));
        next.delete(batchId);
        changed = true;
        return;
      }

      if (now - batch.createdAt < MIN_RECONCILE_AGE_MS) {
        return;
      }

      const settled = Array.from(batch.pendingFileIds).filter(fileId => !running.has(fileId));
      if (settled.length === 0) {
        return;
      }

      settled.forEach(fileId => batch.pendingFileIds.delete(fileId));
      changed = true;

      if (batch.pendingFileIds.size === 0) {
        finished.push(this.toProgress(batch));
        next.delete(batchId);
      }
    });

    if (!changed) {
      return;
    }

    this.batchesSubject.next(next);
    finished.forEach(batch => this.batchCompletedSubject.next(batch));
  }

  public activeBatchForProject$(projectId: number): Observable<ReclassificationBatchProgress | null> {
    return this.batches$.pipe(
      map(batches =>
        batches
          .filter(batch => batch.projectId === projectId)
          .sort((left, right) => right.createdAt - left.createdAt)[0] ?? null,
      ),
    );
  }

  private collectPendingFileIds(batches: Map<string, ReclassificationBatchState>, projectId: number): Set<string> {
    const ids = new Set<string>();
    const now = Date.now();
    batches.forEach(batch => {
      if (batch.projectId !== projectId) return;
      if (now - batch.createdAt > MAX_BATCH_AGE_MS) return;
      batch.pendingFileIds.forEach(id => ids.add(id));
    });
    return ids;
  }

  private onFileCompleted(fileId: string): void {
    if (!fileId) {
      return;
    }

    const next = new Map(this.batchesSubject.value);
    let changed = false;
    const finished: ReclassificationBatchProgress[] = [];

    next.forEach((batch, batchId) => {
      if (!batch.pendingFileIds.has(fileId)) {
        return;
      }

      batch.pendingFileIds.delete(fileId);
      changed = true;

      if (batch.pendingFileIds.size === 0) {
        finished.push(this.toProgress(batch));
        next.delete(batchId);
      }
    });

    if (!changed) {
      return;
    }

    this.batchesSubject.next(next);
    finished.forEach(batch => this.batchCompletedSubject.next(batch));
  }

  private toProgressList(batches: Map<string, ReclassificationBatchState>): ReclassificationBatchProgress[] {
    return Array.from(batches.values())
      .map(batch => this.toProgress(batch))
      .sort((left, right) => right.createdAt - left.createdAt);
  }

  private toProgress(batch: ReclassificationBatchState): ReclassificationBatchProgress {
    const done = Math.max(0, batch.total - batch.pendingFileIds.size);
    const progress = batch.total > 0 ? Math.min(100, Math.round((done / batch.total) * 100)) : 100;
    return {
      batchId: batch.batchId,
      projectId: batch.projectId,
      total: batch.total,
      done,
      progress,
      createdAt: batch.createdAt,
    };
  }

  private computeOverallProgress(batches: ReclassificationBatchProgress[]): ReclassificationOverallProgress {
    if (batches.length === 0) {
      return {progress: 0, done: 0, total: 0};
    }

    const done = batches.reduce((sum, batch) => sum + batch.done, 0);
    const total = batches.reduce((sum, batch) => sum + batch.total, 0);
    const progress = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 100;
    return {progress, done, total};
  }
}
